import { Tooltip } from 'antd';
import { FC, ReactNode } from 'react';

/** Default status colors (from brand guide) */
export const DEFAULT_STATUS_COLORS = {
  low: '#f31a25', // Red - Offtrack
  mid: '#faad14', // Yellow/Orange - At Risk
  high: '#389e0d', // Green - On Track
};

/** Calculate status info based on percent and breakpoints */
export function getStatusFromBreakpoints(
  percent: number,
  breakpoints: IStatusBreakpoints
): { color: string; label: string } {
  const [lowThreshold, midThreshold] = breakpoints.thresholds;
  const colors = breakpoints.colors || DEFAULT_STATUS_COLORS;

  if (percent < lowThreshold) {
    return { color: colors.low, label: breakpoints.labels.low };
  } else if (percent < midThreshold) {
    return { color: colors.mid, label: breakpoints.labels.mid };
  } else {
    return { color: colors.high, label: breakpoints.labels.high };
  }
}

/** Status breakpoints configuration */
export interface IStatusBreakpoints {
  /** [low threshold, mid threshold] - e.g. [50, 75] means 0-50 is low, 50-75 is mid, 75+ is high */
  thresholds: [number, number];
  /** Labels for each status level */
  labels: {
    low: string;
    mid: string;
    high: string;
  };
  /** Colors for each status level */
  colors?: {
    low: string;
    mid: string;
    high: string;
  };
}

export interface IDataStripStackedProps {
  /** Ant Design icon component (optional) */
  icon?: ReactNode;
  /** Primary color (border, bar fill) */
  primaryColor?: string;
  /** Secondary color (bar background) */
  secondaryColor?: string;
  /** Array of data values */
  dataArray: (number | string)[];
  /** Array of optional icons to display to the left of each value */
  dataIcons?: ReactNode[];
  /** Size of data icons in pixels */
  dataIconSize?: number;
  /** Array of labels corresponding to data values */
  dataLabelArray: string[];
  /** Progress bar percentage (0-100) */
  barPercent: number;
  /** Font size for data values */
  valueFontSize?: number;
  /** Color for data values */
  valueColor?: string;
  /** Font size for labels */
  labelFontSize?: number;
  /** Color for labels */
  labelColor?: string;
  /** Height of the progress bar */
  barHeight?: number;
  /** Label displayed above the progress bar */
  barLabel?: string;
  /** Color for the bar label text */
  barLabelColor?: string;
  /** Font size for the bar label text */
  barLabelTextSize?: number;
  /** Show trapezoid shapes behind labels */
  showLabelTrapezoids?: boolean;
  /** Trapezoid background color */
  trapezoidColor?: string;
  /** Trapezoid height */
  trapezoidHeight?: number;
  /** Extra padding on each side of trapezoid (makes it wider) */
  trapezoidPadding?: number;
  /** Adjustment to calculated trapezoid width (positive = wider, negative = narrower) */
  trapezoidWidthBump?: number;
  /** Show ribbon banners on top of trapezoids with labels inside */
  showRibbons?: boolean;
  /** Ribbon background color */
  ribbonColor?: string;
  /** Ribbon text color */
  ribbonTextColor?: string;
  /** Ribbon font size */
  ribbonFontSize?: number;
  /** Ribbon vertical padding */
  ribbonPaddingY?: number;
  /** Ribbon horizontal padding */
  ribbonPaddingX?: number;
  /** Ribbon border color (defaults to primaryColor) */
  ribbonBorderColor?: string;
  /** Progress bar fill color (defaults to primaryColor) */
  barColor?: string;
  /** Units so far (actual), displayed above progress bar */
  unitsSoFar?: number;
  /** Label for units so far */
  unitsSoFarLabel?: string;
  /** Color for units so far text */
  unitsSoFarColor?: string;
  /** Background color for units so far pill */
  unitsSoFarBgColor?: string;
  /** Top position offset for units so far pill */
  unitsSoFarTop?: number;
  /** Left position offset for units so far pill */
  unitsSoFarLeft?: number;
  /** Units remaining (goal - actual), displayed above progress bar */
  unitsRemaining?: number;
  /** Label for units remaining */
  unitsRemainingLabel?: string;
  /** Color for units remaining text */
  unitsRemainingColor?: string;
  /** Background color for units remaining pill */
  unitsRemainingBgColor?: string;
  /** Top position offset for units remaining pill */
  unitsRemainingTop?: number;
  /** Left position offset for units remaining pill */
  unitsRemainingLeft?: number;
  /** Gap between the two pills (unitsSoFar and unitsRemaining) */
  unitsPillGap?: number;
  /** Daily pace needed to hit goal */
  dailyPaceNeeded?: number;
  /** Label for daily pace */
  dailyPaceLabel?: string;
  /** Color for daily pace text */
  dailyPaceColor?: string;
  /** Font size for daily pace text */
  dailyPaceFontSize?: number;
  /** Top position for daily pace text */
  dailyPaceTop?: number;
  /** Extra bottom padding to contain absolutely positioned elements like pills */
  extraBottomPadding?: number;
  /** Show status border around the figure based on goal progress */
  showStatusBorder?: boolean;
  /** Status breakpoints configuration for border color and label */
  statusBreakpoints?: IStatusBreakpoints;
  /** Number of working days elapsed (for pace-based status calculation) */
  workingDaysElapsed?: number;
  /** Daily pRBC goal per office (for pace-based status calculation) */
  dailyPrbcGoal?: number;
  /** Center the component horizontally within its container */
  centered?: boolean;
  /** Click handler for value 1 (pRBC so far) - makes ribbon and value clickable */
  onValue1Click?: () => void;
  /** Tooltip for value 1 click action */
  value1ClickTooltip?: string;
}

export const DataStripStacked: FC<IDataStripStackedProps> = ({
  icon,
  primaryColor = '#7B1FA2',
  secondaryColor = '#E1BEE7',
  dataArray,
  dataIcons,
  dataIconSize = 24,
  dataLabelArray,
  barPercent,
  valueFontSize = 48,
  valueColor = '#333',
  labelFontSize = 18,
  labelColor = '#000',
  barHeight = 24,
  barLabel,
  barLabelColor,
  barLabelTextSize,
  showLabelTrapezoids = false,
  trapezoidColor = '#f0f0f0',
  trapezoidHeight = 22,
  trapezoidPadding = 10,
  trapezoidWidthBump = 0,
  showRibbons = false,
  ribbonColor,
  ribbonTextColor = '#fff',
  ribbonFontSize = 12,
  ribbonPaddingY = 4,
  ribbonPaddingX: _ribbonPaddingX = 8,
  ribbonBorderColor,
  barColor,
  unitsSoFar,
  unitsSoFarLabel = 'Units so far:',
  unitsSoFarColor = '#fff',
  unitsSoFarBgColor = '#e85c5c',
  unitsSoFarTop,
  unitsSoFarLeft = 0,
  unitsRemaining,
  unitsRemainingLabel = 'Units needed',
  unitsRemainingColor = '#fff',
  unitsRemainingBgColor = '#e85c5c',
  unitsRemainingTop,
  unitsRemainingLeft = 0,
  unitsPillGap = 8,
  dailyPaceNeeded,
  dailyPaceLabel = 'Daily pace needed to hit goal:',
  dailyPaceColor = '#0b345f',
  dailyPaceFontSize = 15,
  dailyPaceTop,
  extraBottomPadding = 0,
  showStatusBorder = false,
  statusBreakpoints,
  workingDaysElapsed,
  dailyPrbcGoal,
  centered = false,
  onValue1Click,
  value1ClickTooltip,
}) => {
  // Use primaryColor as default ribbon color if not specified
  const effectiveRibbonColor = ribbonColor ?? primaryColor;
  const effectiveRibbonBorderColor = ribbonBorderColor ?? primaryColor;
  const effectiveBarColor = barColor ?? primaryColor;

  // Calculate pace-based status percent: (unitsSoFar / workingDaysElapsed) / dailyPrbcGoal * 100
  // This measures if we're on track based on the daily production rate
  // workingDaysElapsed should only count COMPLETE working days (not including today)
  // If no complete days have elapsed yet but we have units, use 1 day as minimum
  const effectiveElapsedDays =
    workingDaysElapsed !== undefined &&
    workingDaysElapsed <= 0 &&
    unitsSoFar &&
    unitsSoFar > 0
      ? 1
      : workingDaysElapsed;
  const pacePercent =
    effectiveElapsedDays &&
    effectiveElapsedDays > 0 &&
    dailyPrbcGoal &&
    dailyPrbcGoal > 0 &&
    unitsSoFar !== undefined
      ? (unitsSoFar / effectiveElapsedDays / dailyPrbcGoal) * 100
      : barPercent; // Fall back to barPercent if pace data not available

  // Calculate status based on pace percent and breakpoints
  const statusInfo =
    showStatusBorder && statusBreakpoints
      ? getStatusFromBreakpoints(pacePercent, statusBreakpoints)
      : null;

  // Layout constants
  const scaledTrapezoidHeightEarly = trapezoidHeight;
  // Ribbon height calculation - accounts for multi-line labels
  const label1Lines = (dataLabelArray[0] || '').split('\n').length;
  const lineHeight = ribbonFontSize * 1.2;
  const r1Height = label1Lines * lineHeight + ribbonPaddingY * 2;
  // The ribbon extends above the trapezoid by: r1Height - (trapOverlap)
  // where trapOverlap = scaledTrapezoidHeight * 0.75
  const trapOverlapEarly = scaledTrapezoidHeightEarly * 0.75;
  const ribbonAboveTrap = showRibbons
    ? Math.max(0, r1Height - trapOverlapEarly)
    : 0;

  // Status label configuration
  const statusLabelHeight = showStatusBorder && statusInfo ? 20 : 0; // Height for label above border
  const statusDailyPaceHeight =
    showStatusBorder && statusInfo && dailyPaceNeeded !== undefined ? 40 : 0; // Space below border for daily pace

  // Base figure height (content inside border)
  const figureHeight = 115 + (showRibbons ? ribbonAboveTrap + r1Height : 0);

  // Total height includes: label above + border + figure + border + daily pace below + extra padding
  const height =
    statusLabelHeight +
    figureHeight +
    statusDailyPaceHeight +
    extraBottomPadding;
  const strokeWidth = 4;
  const iconWidth = icon ? 100 : 0;
  // Calculate width based on label text, with optional bump adjustment
  const charWidth = labelFontSize * 1.1;
  const value1Width =
    Math.ceil((dataLabelArray[0]?.length || 0) * charWidth) +
    5 +
    trapezoidWidthBump;
  const value2Width =
    Math.ceil((dataLabelArray[1]?.length || 0) * charWidth) +
    5 +
    trapezoidWidthBump;
  const barWidth = 128;
  const totalWidth = iconWidth + value1Width + value2Width + barWidth;

  // Vertical positions
  // verticalOffset pushes everything down to make room for the ribbon above
  const verticalOffset = showRibbons ? ribbonAboveTrap : 0;
  const topY = 30 + verticalOffset;
  const bottomY = 90 + verticalOffset;
  const cornerRadius = 5;
  const zSlant = 10;

  // Trapezoid dimensions
  const scaledTrapezoidHeight = trapezoidHeight;
  const scaledTrapezoidPadding = trapezoidPadding;

  // Calculate trapezoid slants to match the Z-line angles
  const zLineHorizontalSpan = 2 * zSlant;
  const z1VerticalSpan = bottomY - topY;
  const matchedTrapezoid1Slant =
    (zLineHorizontalSpan / z1VerticalSpan) * scaledTrapezoidHeight;
  const matchedTrapezoid2Slant =
    (zLineHorizontalSpan / z1VerticalSpan) * scaledTrapezoidHeight;

  // Horizontal positions
  const x1 = iconWidth;
  const x2 = x1 + value1Width;
  const x3 = x2 + value2Width;

  const r = cornerRadius;
  const pad = strokeWidth / 2;
  const leftPad = showLabelTrapezoids ? scaledTrapezoidPadding : 0;

  // Bar height
  const scaledBarHeight = barHeight;

  // Bar position - to the right of trapezoid 1
  const barStartX = leftPad + pad + x2 + zSlant + matchedTrapezoid1Slant;
  const barY = topY;

  // Calculate the visible figure width (excludes the progress bar area for border purposes)
  // The Z-shape ends at x3 + zSlant, plus the trapezoid2 slant extension
  // Add padding: 4px left + 8px right (4 more on right)
  const borderPaddingLeft = 5;
  const borderPaddingRight = 1;
  const visibleFigureWidth =
    borderPaddingLeft +
    leftPad +
    pad +
    x3 +
    zSlant +
    matchedTrapezoid2Slant +
    scaledTrapezoidPadding +
    borderPaddingRight;

  const content = (
    <div
      style={{
        position: 'relative',
        width: visibleFigureWidth,
        height,
      }}
    >
      {/* Status label above figure - parallelogram pill */}
      {showStatusBorder && statusInfo && (
        <div
          style={{
            position: 'absolute',
            top: -5,
            left: 0,
            width: visibleFigureWidth,
            height: statusLabelHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: statusInfo.color,
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              padding: '3px 12px',
              transform: 'skewX(-12deg)',
              borderRadius: 3,
            }}
          >
            <span
              style={{ display: 'inline-block', transform: 'skewX(12deg)' }}
            >
              {statusInfo.label}
            </span>
          </div>
        </div>
      )}

      {/* Main figure container */}
      <div
        style={{
          position: 'absolute',
          top: statusLabelHeight,
          left: 0,
          width: totalWidth + pad * 2 + leftPad,
          height: figureHeight,
          filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.15))',
        }}
      >
        {/* SVG */}
        <svg
          width={totalWidth + pad * 2 + leftPad}
          height={figureHeight}
          style={{ position: 'absolute', top: 0, left: 0 }}
        >
          {/* Trapezoid 1 (above topY line) */}
          {showLabelTrapezoids &&
            (() => {
              const tr = 3;
              const t1TopLeft = {
                x: leftPad + pad + x1 - scaledTrapezoidPadding,
                y: topY - scaledTrapezoidHeight,
              };
              const t1TopRight = {
                x: leftPad + pad + x2 + zSlant + matchedTrapezoid1Slant,
                y: topY - scaledTrapezoidHeight,
              };
              const t1BottomRight = {
                x: leftPad + pad + x2 + zSlant,
                y: topY + strokeWidth / 2,
              };
              const t1BottomLeft = {
                x:
                  leftPad +
                  pad +
                  x1 -
                  scaledTrapezoidPadding +
                  matchedTrapezoid1Slant,
                y: topY + strokeWidth / 2,
              };
              return (
                <path
                  d={`
                M ${t1TopLeft.x + tr},${t1TopLeft.y}
                L ${t1TopRight.x - tr},${t1TopRight.y}
                Q ${t1TopRight.x},${t1TopRight.y} ${t1TopRight.x},${
                    t1TopRight.y + tr
                  }
                L ${t1BottomRight.x},${t1BottomRight.y}
                L ${t1BottomLeft.x},${t1BottomLeft.y}
                L ${t1TopLeft.x},${t1TopLeft.y + tr}
                Q ${t1TopLeft.x},${t1TopLeft.y} ${t1TopLeft.x + tr},${
                    t1TopLeft.y
                  }
                Z
              `}
                  fill={trapezoidColor}
                />
              );
            })()}

          {/* Trapezoid 2 (below bottomY line) */}
          {showLabelTrapezoids &&
            (() => {
              const tr = 3;
              const t2TopLeft = {
                x: leftPad + pad + x2 - zSlant,
                y: bottomY - strokeWidth / 2,
              };
              const t2TopRight = {
                x: leftPad + pad + x3 + scaledTrapezoidPadding,
                y: bottomY - strokeWidth / 2,
              };
              const t2BottomRight = {
                x:
                  leftPad +
                  pad +
                  x3 +
                  scaledTrapezoidPadding +
                  matchedTrapezoid2Slant,
                y: bottomY + scaledTrapezoidHeight,
              };
              const t2BottomLeft = {
                x: leftPad + pad + x2 - zSlant - matchedTrapezoid2Slant,
                y: bottomY + scaledTrapezoidHeight,
              };
              return (
                <path
                  d={`
                M ${t2TopLeft.x},${t2TopLeft.y}
                L ${t2TopRight.x},${t2TopRight.y}
                L ${t2BottomRight.x},${t2BottomRight.y - tr}
                Q ${t2BottomRight.x},${t2BottomRight.y} ${
                    t2BottomRight.x - tr
                  },${t2BottomRight.y}
                L ${t2BottomLeft.x + tr},${t2BottomLeft.y}
                Q ${t2BottomLeft.x},${t2BottomLeft.y} ${t2BottomLeft.x},${
                    t2BottomLeft.y - tr
                  }
                Z
              `}
                  fill={trapezoidColor}
                />
              );
            })()}

          {/* Z-line path - only goes from trap1 to trap2, no bar section */}
          <path
            d={`
            M ${leftPad + pad + x1} ${topY + 0}
            Q ${leftPad + pad + x1} ${topY} ${leftPad + pad + x1 + r} ${topY}
            L ${leftPad + pad + x2 + zSlant - r} ${topY}
            Q ${leftPad + pad + x2 + zSlant} ${topY} ${
              leftPad + pad + x2 + zSlant
            } ${topY + r}
            L ${leftPad + pad + x2 - zSlant} ${bottomY - r}
            Q ${leftPad + pad + x2 - zSlant} ${bottomY} ${
              leftPad + pad + x2 - zSlant + r
            } ${bottomY}
            L ${leftPad + pad + x3 + zSlant - r} ${bottomY}
            Q ${leftPad + pad + x3 + zSlant} ${bottomY} ${
              leftPad + pad + x3 + zSlant
            } 
          `}
            fill="none"
            stroke={primaryColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Ribbon 1 (on top of trapezoid 1) */}
          {showRibbons &&
            showLabelTrapezoids &&
            (() => {
              const label = dataLabelArray[0] || '';
              const lines = label.split('\n');
              const lineHeight = ribbonFontSize * 1.2;
              const r1Height = lines.length * lineHeight + ribbonPaddingY * 2;
              const trapTopY = topY - scaledTrapezoidHeight;
              const trapLeftX = leftPad + pad + x1 - scaledTrapezoidPadding;
              const trapRightX =
                leftPad + pad + x2 + zSlant + matchedTrapezoid1Slant;
              const trapWidth = trapRightX - trapLeftX;
              const ribbonInset = trapWidth * 0.15;
              const ribbonLeftX = trapLeftX + ribbonInset;
              const ribbonRightX = trapRightX - ribbonInset;
              const trapOverlap = scaledTrapezoidHeight * 0.75;
              const ribbonY = trapTopY + trapOverlap - r1Height;
              const rr = 3;
              const borderWidth = 2;
              const isClickable = !!onValue1Click;

              const ribbonContent = (
                <g
                  style={{ cursor: isClickable ? 'pointer' : undefined }}
                  onClick={isClickable ? onValue1Click : undefined}
                >
                  <rect
                    x={ribbonLeftX}
                    y={ribbonY}
                    width={ribbonRightX - ribbonLeftX}
                    height={r1Height}
                    rx={rr}
                    ry={rr}
                    fill={effectiveRibbonColor}
                    stroke={effectiveRibbonBorderColor}
                    strokeWidth={borderWidth}
                  />
                  <text
                    x={(ribbonLeftX + ribbonRightX) / 2}
                    y={ribbonY + ribbonPaddingY + lineHeight * 0.8}
                    fill={ribbonTextColor}
                    fontSize={ribbonFontSize}
                    fontWeight={500}
                    textAnchor="middle"
                  >
                    {lines.map((line, i) => (
                      <tspan
                        key={i}
                        x={(ribbonLeftX + ribbonRightX) / 2}
                        dy={i === 0 ? 0 : lineHeight}
                      >
                        {line}
                      </tspan>
                    ))}
                  </text>
                </g>
              );

              if (isClickable && value1ClickTooltip) {
                return (
                  <Tooltip
                    title={value1ClickTooltip}
                    overlayInnerStyle={{
                      background: '#2f3a45',
                      border: '1px solid #0b345f',
                      borderRadius: 3,
                    }}
                    color="#2f3a45"
                  >
                    {ribbonContent}
                  </Tooltip>
                );
              }
              return ribbonContent;
            })()}

          {/* Ribbon 2 (below trapezoid 2) */}
          {showRibbons &&
            showLabelTrapezoids &&
            (() => {
              const label = dataLabelArray[1] || '';
              const lines = label.split('\n');
              const lineHeight = ribbonFontSize * 1.2;
              const r2Height = lines.length * lineHeight + ribbonPaddingY * 2;
              const trapBottomY = bottomY + scaledTrapezoidHeight;
              const trapLeftX =
                leftPad + pad + x2 - zSlant - matchedTrapezoid2Slant;
              const trapRightX =
                leftPad +
                pad +
                x3 +
                scaledTrapezoidPadding +
                matchedTrapezoid2Slant;
              const trapWidth = trapRightX - trapLeftX;
              const ribbonInset = trapWidth * 0.17;
              const ribbonLeftX = trapLeftX + ribbonInset;
              const ribbonRightX = trapRightX - ribbonInset;
              const trapOverlap = scaledTrapezoidHeight * 0.75;
              const ribbonY = trapBottomY - trapOverlap;
              const rr = 3;
              const borderWidth = 2;
              return (
                <g>
                  <rect
                    x={ribbonLeftX}
                    y={ribbonY}
                    width={ribbonRightX - ribbonLeftX}
                    height={r2Height}
                    rx={rr}
                    ry={rr}
                    fill={effectiveRibbonColor}
                    stroke={effectiveRibbonBorderColor}
                    strokeWidth={borderWidth}
                  />
                  <text
                    x={(ribbonLeftX + ribbonRightX) / 2}
                    y={ribbonY + ribbonPaddingY + lineHeight * 0.8}
                    fill={ribbonTextColor}
                    fontSize={ribbonFontSize}
                    fontWeight={500}
                    textAnchor="middle"
                  >
                    {lines.map((line, i) => (
                      <tspan
                        key={i}
                        x={(ribbonLeftX + ribbonRightX) / 2}
                        dy={i === 0 ? 0 : lineHeight}
                      >
                        {line}
                      </tspan>
                    ))}
                  </text>
                </g>
              );
            })()}
        </svg>

        {/* Icon */}
        {icon && (
          <div
            style={{
              position: 'absolute',
              left: leftPad + pad,
              top: bottomY - 80,
              fontSize: 90,
            }}
          >
            {icon}
          </div>
        )}

        {/* Value 1 (under the top line) */}
        <div
          style={{
            position: 'absolute',
            left: leftPad + pad + x1,
            top: 0,
            width: value1Width,
            textAlign: 'center',
          }}
        >
          {!showRibbons && (
            <div
              style={{
                fontSize: labelFontSize,
                color: labelColor,
                position: 'absolute',
                top: 14,
                width: '100%',
                lineHeight: 1,
                whiteSpace: 'pre-line',
              }}
            >
              {dataLabelArray[0] || ''}
            </div>
          )}
          {(() => {
            const isClickable = !!onValue1Click;
            const valueContent = (
              <div
                style={{
                  fontSize: valueFontSize,
                  fontWeight: 700,
                  color: valueColor,
                  position: 'absolute',
                  // Position Value 1 - top of text close to top line (moved up ~25px)
                  top: topY - 7,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  cursor: isClickable ? 'pointer' : undefined,
                  textDecoration: isClickable ? 'underline' : undefined,
                }}
                onClick={isClickable ? onValue1Click : undefined}
              >
                {dataIcons?.[0] && (
                  <span style={{ fontSize: dataIconSize, display: 'flex' }}>
                    {dataIcons[0]}
                  </span>
                )}
                {dataArray[0]}
              </div>
            );

            if (isClickable && value1ClickTooltip) {
              return (
                <Tooltip
                  title={value1ClickTooltip}
                  overlayInnerStyle={{
                    background: '#2f3a45',
                    border: '1px solid #0b345f',
                    borderRadius: 3,
                  }}
                  color="#2f3a45"
                >
                  {valueContent}
                </Tooltip>
              );
            }
            return valueContent;
          })()}

          {/* Units so far pill - parallelogram */}
          {unitsSoFar !== undefined && (
            <div
              style={{
                position: 'absolute',
                top: unitsSoFarTop ?? topY + 5 + valueFontSize + 5,
                left: unitsSoFarLeft,
                display: 'inline-flex',
                backgroundColor: unitsSoFarBgColor,
                borderRadius: 4,
                padding: '6px 14px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                transform: 'skew(-18deg)',
              }}
            >
              <div
                style={{
                  transform: 'skew(18deg)',
                  fontSize: barLabelTextSize
                    ? barLabelTextSize * 1.1
                    : labelFontSize * 0.8,
                  color: unitsSoFarColor,
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                <div style={{ lineHeight: 1.1 }}>{unitsSoFarLabel}</div>
                <div style={{ fontSize: '120%', lineHeight: 1.1 }}>
                  {unitsSoFar}
                </div>
              </div>
            </div>
          )}

          {/* Units remaining pill - parallelogram */}
          {unitsRemaining !== undefined && (
            <div
              style={{
                position: 'absolute',
                top:
                  unitsRemainingTop ??
                  topY +
                    5 +
                    valueFontSize +
                    5 +
                    (barLabelTextSize ?? labelFontSize * 0.8) * 2 +
                    unitsPillGap +
                    18,
                left: unitsRemainingLeft,
                display: 'inline-flex',
                backgroundColor: statusInfo?.color ?? unitsRemainingBgColor,
                borderRadius: 4,
                padding: '6px 14px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                transform: 'skew(-18deg)',
              }}
            >
              <div
                style={{
                  transform: 'skew(18deg)',
                  fontSize: barLabelTextSize
                    ? barLabelTextSize * 1.1
                    : labelFontSize * 0.8,
                  color: unitsRemainingColor,
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                <div style={{ lineHeight: 1.1 }}>{unitsRemainingLabel}</div>
                <div style={{ fontSize: '120%', lineHeight: 1.1 }}>
                  {unitsRemaining}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Value 2 (above the bottom line) */}
        <div
          style={{
            position: 'absolute',
            left: leftPad + pad + x2,
            top: 0,
            width: value2Width,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: valueFontSize,
              fontWeight: 700,
              color: valueColor,
              position: 'absolute',
              // Position Value 2 - bottom of text is 15px above the bottom line
              top: bottomY - 15 - valueFontSize,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
            }}
          >
            {dataIcons?.[1] && (
              <span style={{ fontSize: dataIconSize, display: 'flex' }}>
                {dataIcons[1]}
              </span>
            )}
            {dataArray[1]}
          </div>
          {!showRibbons && (
            <div
              style={{
                fontSize: labelFontSize,
                color: labelColor,
                position: 'absolute',
                top: bottomY + 1,
                width: '100%',
                whiteSpace: 'pre-line',
              }}
            >
              {dataLabelArray[1] || ''}
            </div>
          )}
        </div>

        {/* Bar label above the progress bar */}
        {barLabel && (
          <div
            style={{
              position: 'absolute',
              left: barStartX + 3,
              top:
                barY -
                scaledBarHeight -
                2 * 1.1 -
                (barLabelTextSize ?? labelFontSize * 0.8) -
                6,
              width: barWidth,
              fontSize: barLabelTextSize ?? labelFontSize * 0.8,
              color: barLabelColor ?? primaryColor,
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
          >
            {barLabel} <span style={{ fontSize: '120%' }}>{barPercent}%</span>
            {/* → */}
          </div>
        )}

        {/* Progress bar - parallelogram */}
        <div
          style={{
            position: 'absolute',
            left: barStartX,
            top: barY - scaledBarHeight - 2 * 1,
            width: barWidth,
            height: scaledBarHeight,
            backgroundColor: secondaryColor,
            overflow: 'hidden',
            transform: 'skewX(-17deg)',
          }}
        >
          <div
            style={{
              width: `${Math.min(100, Math.max(0, barPercent))}%`,
              height: '100%',
              backgroundColor: effectiveBarColor,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Daily pace needed - positioned below the border */}
      {dailyPaceNeeded !== undefined && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: dailyPaceTop ?? statusLabelHeight + figureHeight + 4,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              fontSize: dailyPaceFontSize,
              color: dailyPaceColor,
              whiteSpace: 'nowrap',
              fontWeight: 600,
              backgroundColor: effectiveRibbonColor,
              border: `2px solid ${effectiveRibbonBorderColor}`,
              borderRadius: 4,
              padding: '3px 6px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
            }}
          >
            <span style={{ color: ribbonTextColor }}>
              {dailyPaceLabel}{' '}
              <strong
                style={{
                  fontSize: '140%',
                  color: statusInfo?.color ?? ribbonTextColor,
                }}
              >
                {dailyPaceNeeded > 0 ? dailyPaceNeeded : '∞'}
              </strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );

  if (centered) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        {content}
      </div>
    );
  }

  return content;
};

export default DataStripStacked;
